// Copyright (c) Ping. All rights reserved.

import type React from 'react';
import {
  StyleSheet,
  Text,
  type TextStyle,
  View,
  type ViewStyle,
} from 'react-native';
// Class-aware scheme hook (reads `<html data-theme>` on web / NativeWind
// observable on desktop) — NOT RN's `useColorScheme`, which reads the OS
// `prefers-color-scheme` and renders the wrong palette under the app's
// class-based theme (repo styling rule 3).
import { useColorScheme } from '../foundations/colorScheme';
import { createTheme } from '../design-system/theme';
import { threadsStrings } from './strings';

export interface ThreadEmptyStateProps {
  readonly testID?: string;
}

/** Shown when the open thread has zero replies. */
export const ThreadEmptyState: React.FC<ThreadEmptyStateProps> = ({ testID }) => {
  const scheme = useColorScheme();
  const colorScheme: 'light' | 'dark' = scheme === 'dark' ? 'dark' : 'light';
  const theme = createTheme(colorScheme);

  return (
    <View
      style={styles.container}
      accessibilityRole="text"
      accessibilityLabel={threadsStrings.ping_threads_empty_title}
      testID={testID}
    >
      <Text style={[styles.title, { color: theme.colors.text.primary }]}>
        {threadsStrings.ping_threads_empty_title}
      </Text>
      <Text style={[styles.body, { color: theme.colors.text.secondary }]}>
        {threadsStrings.ping_threads_empty_body}
      </Text>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 24,
    paddingVertical: 48,
    gap: 8,
  } satisfies ViewStyle,
  title: {
    fontSize: 17,
    fontWeight: '700',
    textAlign: 'center',
  } satisfies TextStyle,
  body: {
    fontSize: 14,
    fontWeight: '400',
    textAlign: 'center',
    lineHeight: 20,
  } satisfies TextStyle,
});
